// src/components/order/OrderCard.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency, formatDateTime } from '../../utils/helpers';
import { ORDER_STATUS_COLORS, getSizeLabel } from '../../utils/constants';

/**
 * Order Card Component
 * 
 * Used in: OrdersPage (Order History)
 * 
 * Features:
 * - Order summary in list view
 * - Order status badge
 * - Quick order details
 * - Link to full order details
 * - Cancel order option
 */
const OrderCard = ({ order, onCancelOrder }) => {
  const canCancel = ['PENDING', 'CONFIRMED'].includes(order.status);
  
  // Get first few items for preview
  const displayItems = order.items?.slice(0, 2) || [];
  const remainingCount = (order.items?.length || 0) - displayItems.length;
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Order #{order.orderNumber}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Placed on {formatDateTime(order.createdAt)}
          </p>
        </div>
        
        {/* Order Status Badge */}
        <span className={`
          inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold
          ${ORDER_STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'}
        `}>
          {order.status}
        </span>
      </div>
      
      {/* Order Items Preview */}
      <div className="border-t border-gray-200 pt-4 mb-4">
        <div className="space-y-3">
          {displayItems.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <img
                src={item.productImage || '/placeholder-product.png'}
                alt={item.productName}
                className="w-16 h-16 object-cover rounded-md"
              />
              <div className="flex-grow">
                <p className="font-medium text-gray-900">{item.productName}</p>
                <p className="text-sm text-gray-600">
                  Size: {getSizeLabel(item.size)} • Qty: {item.quantity}
                </p>
                
                {/* ⭐ Show Custom Name / Passing Year if present */}
                {(item.customName || item.passingYear) && (
                  <p className="text-sm text-blue-600 font-medium">
                    {item.customName
                      ? `Print: ${item.customName}${item.passingYear ? ` • ${item.passingYear}` : ''}`
                      : `Passing year: ${item.passingYear}`}
                  </p>
                )}
              </div>
              <p className="font-semibold text-gray-900">
                {formatCurrency(item.subtotal)}
              </p>
            </div>
          ))}
          
          {/* Remaining items indicator */}
          {remainingCount > 0 && (
            <p className="text-sm text-gray-600">
              + {remainingCount} more item{remainingCount > 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
      
      {/* Order Total */}
      <div className="border-t border-gray-200 pt-4 mb-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-700">Total Amount</span>
          <span className="text-xl font-bold text-gray-900">
            {formatCurrency(order.totalAmount)}
          </span>
        </div>
        
        {/* Payment Method */}
        <div className="flex justify-between items-center mt-2">
          <span className="text-sm text-gray-600">Payment Method</span>
          <span className="text-sm font-medium text-gray-700">
            {order.paymentMethod === 'RAZORPAY' ? 'Online Payment' : 'Cash on Delivery'}
          </span>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex gap-3">
        <Link
          to={`/orders/${order.id}`}
          className="flex-1 text-center py-2 px-4 border-2 border-blue-600 text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
        >
          View Details
        </Link>
        
        {canCancel && (
          <button
            onClick={() => onCancelOrder(order.id)}
            className="flex-1 py-2 px-4 border-2 border-red-600 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors"
          >
            Cancel Order
          </button>
        )}
      </div>
    </div>
  );
};

export default OrderCard;